import { useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import MobxWrap from '@/components/MobxWrap';
import { ProductTracker } from '@/utils/Tracker';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import VersionList from './components/VersionList';
import { getVersionConfig, hasDoraemon } from '../../config/ProductVersion.config';
import styles from './index.module.less';
import VersionCommodityList from './components/VersionCommodityList';
import WhatsInTheBox from '../ProductDiff/DiffTableModal';
import classNames from 'classnames';

/**
 * 产品规格、套餐
 */
const VersionWrapper = inject(
  'area',
  'product',
  'app'
)(
  observer((props) => {
    const {
      productDetail: { VersionConfig, is_camera, id: pid, commodities },
      packingListPoints = {},
    } = props.product;
    const {
      LANG,
      country: { code },
    } = props.area;
    const { className } = props;

    /**
     * 根据配置文件中配置的数组topAttrIndexList，将原本在套餐底部的属性移至套餐上方
     * topAttrList 套餐上方属性数组
     * bottomAttrList 套餐下方的属性数组
     */
    const [topAttrList, bottomAttrList] = useMemo(() => {
      const topAttrList = [];
      const bottomAttrList = [];
      let topIndexList = getVersionConfig(pid, LANG)?.topAttrIndexList ?? [];
      const isNeedChangeList = getVersionConfig(pid, LANG)?.isNeedChangeAttrList ?? false;

      // GO3s只有在哆啦A梦销售地区才需要交换位置,其他情况不需要变更属性顺序,因此此处添加额外特殊处理
      const isDoraemonArea = hasDoraemon(
        code,
        commodities.map?.((o) => o?.id)
      );

      // 判断是否需要改变套餐属性顺序,如不满足改变套餐属性顺序条件,则将topAttrList置空,后续处理逻辑无效
      if (!(isNeedChangeList || isDoraemonArea)) {
        topIndexList = [];
      }

      VersionConfig.attrList?.forEach?.((groupList, index) => {
        if (topIndexList.includes?.(index + 1)) {
          topAttrList.push({
            groupList,
            itemIndex: index,
          });
        } else {
          bottomAttrList.push({
            groupList,
            itemIndex: index,
          });
        }
      });
      return [topAttrList, bottomAttrList];
    }, [VersionConfig.attrList, pid, code]);

    if (!VersionConfig) return null;
    return (
      <section
        id='ProductVersionInfo'
        className={classNames(styles.ProductVersionInfo, !is_camera && styles.accessory, className)}
      >
        {/* 规格 */}
        <ComponentsErrorBoundary name='TopInfo Version-specification'>
          <VersionList type='specification' isSpecificationGroup />
        </ComponentsErrorBoundary>

        {/* 套餐顶部的属性 */}
        <ComponentsErrorBoundary name='TopInfo Version-attribute-top'>
          {topAttrList?.map((attrItem) => {
            return (
              <VersionList type='attribute' isAttributeGroup key={attrItem.itemIndex} isTopAttrList {...attrItem} />
            );
          })}
        </ComponentsErrorBoundary>

        {/* 套餐 */}
        <ComponentsErrorBoundary name='TopInfo VersionCommodityList'>
          <VersionCommodityList />
        </ComponentsErrorBoundary>

        {/* 包装清单弹窗（只在相机页面显示） */}
        {packingListPoints?.commodityGroup?.length > 1 && <WhatsInTheBox />}

        {/* flow适配性超链接 */}
        <FlowCompatibility />

        {/* 套餐底部的属性 */}
        <ComponentsErrorBoundary name='TopInfo Version-attribute-bottom'>
          {bottomAttrList?.map((attrItem) => {
            return (
              <VersionList
                type='attribute'
                isAttributeGroup
                key={attrItem.itemIndex}
                isTopAttrList={false}
                {...attrItem}
              />
            );
          })}
        </ComponentsErrorBoundary>
      </section>
    );
  })
);

const FlowCompatibility = MobxWrap(['area', 'product'], (props) => {
  const { LANG } = props.area;
  const { productDetail } = props.product;

  if (productDetail.id !== 22) return null;

  const handleLinkClick = () => {
    ProductTracker.productPage({
      action: 'clickFlowCompatibility',
      productState: props.product,
    });
  };

  return (
    <div className={styles.FlowCompatibility}>
      <a
        className={styles.link}
        href={LANG['productNew.module_flow_subTitle_pdf_url']}
        onClick={handleLinkClick}
        rel='noopener noreferrer'
        target='_blank'
      >
        {LANG['product.fmg.mobilePhoneCompatibilityList']}
      </a>
    </div>
  );
});

export default VersionWrapper;
